/**
 * Local Embeddings Module
 *
 * Handles browser-side semantic search using Transformers.js with worker-owned asynchronous caching.
 * Uses a hidden iframe to isolate model loading from Vite's dev server.
 *
 * Architecture:
 * 1. First load: Fetch queries from server, generate and cache embeddings in the worker
 * 2. Subsequent loads: Load from Cache Storage, validate version, search locally
 *
 * Cache structure in worker-owned Cache Storage:
 * - per-client entry: { version, queries, embeddings, cachedAt }
 */

import { fetchAllQueries, fetchQueriesVersion } from './api';
import { normalizeQueryForMatching } from './queryNormalization';

// Debug logging - only in dev mode
const debug = import.meta.env.DEV ? (...args) => console.log(...args) : () => {};

const CACHE_KEY_PREFIX = 'autocomplete_cache_';
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
// A versioned bridge also invalidates copies cached before revalidation headers.
const IFRAME_PATH = '/embedding-iframe.html?v=2';
const DEV_IFRAME_URL = `http://localhost:8080${IFRAME_PATH}`;
const IFRAME_INIT_TIMEOUT_MS = 10000;
const IFRAME_PROBE_TIMEOUT_MS = 1500;
const LOCAL_EMBEDDINGS_UNAVAILABLE = 'EMBEDDING_IFRAME_UNAVAILABLE';

// Iframe state
let iframe = null;
let iframeReady = false;
let iframeInitPromise = null;
let iframeAvailabilityPromise = null;
let requestId = 0;
const pendingRequests = new Map();

export function isLocalEmbeddingsUnavailableError(error) {
  return error?.code === LOCAL_EMBEDDINGS_UNAVAILABLE;
}

/** Remove obsolete embedding caches without parsing their large JSON payloads. */
function removeLegacyCaches() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_KEY_PREFIX)) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch (error) {
    console.error('💾 [CACHE] Error removing obsolete autocomplete caches:', error);
  }
}

async function loadCache(clientId) {
  try {
    return await iframeRequest('loadCache', { clientId });
  } catch (error) {
    console.error('💾 [CACHE] Error loading worker cache:', error);
    return null;
  }
}

async function saveCache(clientId, version, queries, embeddings) {
  try {
    return await iframeRequest('saveCache', {
      clientId,
      data: { version, queries, embeddings, cachedAt: Date.now() },
    });
  } catch (error) {
    // Persistence remains best-effort; initialized in-memory search still works.
    console.error('💾 [CACHE] Error saving worker cache:', error);
    return false;
  }
}

export async function clearCache(clientId) {
  await initIframe();
  return iframeRequest('clearCache', { clientId });
}

/**
 * Initialize the embedding iframe.
 * The iframe must be served from a non-Vite server (port 8080) to avoid fetch interception.
 */
function initIframe() {
  if (iframe) {
    return iframeInitPromise;
  }

  debug(`🖼️ [IFRAME] Creating embedding iframe...`);

  const iframeSrc = import.meta.env.DEV ? DEV_IFRAME_URL : IFRAME_PATH;

  iframeInitPromise = (async () => {
    if (import.meta.env.DEV) {
      const isAvailable = await probeDevIframe();
      if (!isAvailable) {
        const error = new Error('Local embedding iframe unavailable');
        error.code = LOCAL_EMBEDDINGS_UNAVAILABLE;
        throw error;
      }
    }

    let timeoutId = null;

    return new Promise((resolve, reject) => {
      // Create hidden iframe
      iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.id = 'embedding-service';
      iframe.src = iframeSrc;

      // Handle messages from iframe
      const messageHandler = (event) => {
        // Only accept messages from our iframe (check iframe exists first)
        if (!iframe || event.source !== iframe.contentWindow) return;

        const { id, success, result, error, type } = event.data;

        // Handle ready signal
        if (type === 'ready') {
          debug(`🖼️ [IFRAME] Embedding service ready`);
          iframeReady = true;
          if (timeoutId) clearTimeout(timeoutId);
          resolve();
          return;
        }

        // Handle progress updates
        if (type === 'progress') {
          debug(`🖼️ [IFRAME] Progress: ${event.data.current}/${event.data.total}`);
          return;
        }

        // Handle request responses
        const pending = pendingRequests.get(id);
        if (pending) {
          pendingRequests.delete(id);
          if (success) {
            pending.resolve(result);
          } else {
            pending.reject(new Error(error));
          }
        }
      };

      window.addEventListener('message', messageHandler);

      // Handle load errors
      iframe.onerror = (error) => {
        console.error(`🖼️ [IFRAME] Load error:`, error);
        if (timeoutId) clearTimeout(timeoutId);
        reject(new Error('Failed to load embedding iframe'));
      };

      // Timeout for iframe load
      timeoutId = setTimeout(() => {
        if (!iframeReady) {
          const hint = import.meta.env.DEV ? ' - is python server running on port 8080?' : '';
          reject(new Error('Embedding iframe timeout' + hint));
        }
      }, IFRAME_INIT_TIMEOUT_MS);

      document.body.appendChild(iframe);
    });
  })();

  iframeInitPromise.catch((error) => {
    if (isLocalEmbeddingsUnavailableError(error)) {
      debug(`🖼️ [IFRAME] Dev iframe is unavailable, using server fallback`);
    } else {
      console.error(`🖼️ [IFRAME] Initialization failed:`, error);
    }
    if (iframe) {
      iframe.remove();
      iframe = null;
    }
    iframeReady = false;
    iframeInitPromise = null;
  });

  return iframeInitPromise;
}

async function probeDevIframe() {
  if (!import.meta.env.DEV) {
    return true;
  }

  if (iframeAvailabilityPromise) {
    return iframeAvailabilityPromise;
  }

  iframeAvailabilityPromise = (async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), IFRAME_PROBE_TIMEOUT_MS);

    try {
      await fetch(DEV_IFRAME_URL, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      return true;
    } catch (_error) {
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  })();

  return iframeAvailabilityPromise;
}

/**
 * Send request to iframe and wait for response.
 */
function iframeRequest(action, payload = {}) {
  return new Promise((resolve, reject) => {
    if (!iframe || !iframeReady) {
      reject(new Error('Iframe not initialized'));
      return;
    }

    const id = ++requestId;
    pendingRequests.set(id, { resolve, reject });
    iframe.contentWindow.postMessage({ id, action, payload }, '*');
  });
}

/**
 * Generate embedding for a single text via iframe.
 */
async function embedText(text) {
  await initIframe();
  return iframeRequest('embed', { text });
}

/**
 * Generate embeddings for multiple texts via iframe.
 */
async function embedTexts(texts) {
  await initIframe();
  return iframeRequest('embedBatch', { texts });
}

/**
 * Compute cosine similarity between two vectors.
 */
function cosineSimilarity(a, b) {
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }

  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

// Threshold for showing suggestions (embedding cosine similarity)
export const THRESHOLD_HIGH = 0.55;

/**
 * Search queries locally using embeddings.
 */
async function searchLocal(queryText, queries, embeddings, limit = 5, threshold = THRESHOLD_LOW) {
  debug(`🔍 [LOCAL] Searching for: "${queryText}" in ${queries.length} queries`);
  const startTime = performance.now();

  const embedStart = performance.now();
  const queryEmbedding = await embedText(queryText);
  const embedTime = Math.round(performance.now() - embedStart);

  const searchStart = performance.now();
  const scored = [];

  for (let i = 0; i < queries.length; i++) {
    const similarity = cosineSimilarity(queryEmbedding, embeddings[i]);
    if (similarity >= threshold) {
      scored.push({
        ...queries[i],
        similarity: Math.round(similarity * 1000) / 1000,
      });
    }
  }

  scored.sort((a, b) => b.similarity - a.similarity);

  const seen = new Set();
  const deduped = [];
  for (const item of scored) {
    if (!seen.has(item.answer_id)) {
      seen.add(item.answer_id);
      deduped.push(item);
      if (deduped.length >= limit) break;
    }
  }

  const searchTime = Math.round(performance.now() - searchStart);
  const totalTime = Math.round(performance.now() - startTime);

  debug(
    `🔍 [LOCAL] Found ${deduped.length} results in ${totalTime}ms (embed: ${embedTime}ms, search: ${searchTime}ms)`,
  );

  return {
    results: deduped,
    timing: {
      embed_ms: embedTime,
      search_ms: searchTime,
      total_ms: totalTime,
    },
  };
}

/**
 * LocalAutocomplete class - manages cached local semantic search.
 */
export class LocalAutocomplete {
  constructor(clientId) {
    this.clientId = clientId;
    this.queries = null;
    this.embeddings = null;
    this.version = null;
    this.initialized = false;
    this.initializing = false;
    this.initPromise = null;
  }

  _resetState() {
    this.queries = null;
    this.embeddings = null;
    this.version = null;
    this.initialized = false;
    this.initializing = false;
    this.initPromise = null;
  }

  async initialize(onProgress = null) {
    if (this.initialized) {
      debug(`📚 [LOCAL-AC] Already initialized with ${this.queries?.length} queries`);
      return { fromCache: true, queryCount: this.queries?.length || 0 };
    }

    if (this.initializing && this.initPromise) {
      debug(`📚 [LOCAL-AC] Initialization in progress, waiting...`);
      return this.initPromise;
    }

    this.initializing = true;
    debug(`📚 [LOCAL-AC] Initializing for client: ${this.clientId}`);

    this.initPromise = this._doInitialize(onProgress);
    return this.initPromise;
  }

  async ensureFresh(onProgress = null) {
    if (!this.initialized) {
      return this.initialize(onProgress);
    }

    debug(`📚 [LOCAL-AC] Checking freshness for client: ${this.clientId}`);

    try {
      const serverVersion = await fetchQueriesVersion(this.clientId);
      if (serverVersion.version === this.version) {
        debug(`📚 [LOCAL-AC] Local cache is current (${this.version})`);
        return {
          fromCache: true,
          queryCount: this.queries?.length || 0,
          updated: false,
        };
      }

      debug(
        `📚 [LOCAL-AC] Version changed ${this.version} -> ${serverVersion.version}, refreshing...`,
      );

      const previousState = {
        queries: this.queries,
        embeddings: this.embeddings,
        version: this.version,
        initialized: this.initialized,
      };

      this._resetState();

      try {
        const refreshed = await this.initialize(onProgress);
        return {
          ...refreshed,
          updated: true,
        };
      } catch (refreshError) {
        console.error(`📚 [LOCAL-AC] Refresh failed, keeping previous data:`, refreshError);
        this.queries = previousState.queries;
        this.embeddings = previousState.embeddings;
        this.version = previousState.version;
        this.initialized = previousState.initialized;
        this.initializing = false;
        this.initPromise = null;

        return {
          fromCache: true,
          queryCount: this.queries?.length || 0,
          updated: false,
        };
      }
    } catch (error) {
      debug(`📚 [LOCAL-AC] Freshness check failed, keeping current cache:`, error);
      return {
        fromCache: true,
        queryCount: this.queries?.length || 0,
        updated: false,
      };
    }
  }

  async _doInitialize(onProgress) {
    try {
      // Initialize iframe first
      await initIframe();

      // Free the old quota-heavy entries; serialization now happens in the worker.
      removeLegacyCaches();
      const cached = await loadCache(this.clientId);

      if (cached && cached.queries && cached.embeddings) {
        const cacheAge = Date.now() - cached.cachedAt;
        if (cacheAge < CACHE_MAX_AGE_MS) {
          debug(
            `📚 [LOCAL-AC] Cache is fresh (${Math.round(cacheAge / 60000)}min old), validating version...`,
          );

          try {
            const serverVersion = await fetchQueriesVersion(this.clientId);
            if (serverVersion.version === cached.version) {
              debug(`📚 [LOCAL-AC] Cache version matches server, using cache`);
              this.queries = cached.queries;
              this.embeddings = cached.embeddings;
              this.version = cached.version;
              this.initialized = true;
              this.initializing = false;
              return { fromCache: true, queryCount: this.queries.length };
            } else {
              debug(`📚 [LOCAL-AC] Cache version mismatch, refreshing...`);
            }
          } catch (error) {
            debug(`📚 [LOCAL-AC] Version check failed, using stale cache:`, error);
            this.queries = cached.queries;
            this.embeddings = cached.embeddings;
            this.version = cached.version;
            this.initialized = true;
            this.initializing = false;
            return { fromCache: true, queryCount: this.queries.length };
          }
        } else {
          debug(
            `📚 [LOCAL-AC] Cache is stale (${Math.round(cacheAge / 3600000)}h old), refreshing...`,
          );
        }
      }

      // Fetch fresh queries from server
      debug(`📚 [LOCAL-AC] Fetching fresh queries from server...`);
      const { queries, version } = await fetchAllQueries(this.clientId);

      if (!queries || queries.length === 0) {
        debug(`📚 [LOCAL-AC] No queries returned from server`);
        this.queries = [];
        this.embeddings = [];
        this.initialized = true;
        this.initializing = false;
        return { fromCache: false, queryCount: 0 };
      }

      // Generate embeddings locally via iframe
      debug(`📚 [LOCAL-AC] Generating embeddings for ${queries.length} queries...`);
      const texts = queries.map((q) => q.canonical_query);
      const embeddings = await embedTexts(texts);

      // Save to cache
      this.queries = queries;
      this.embeddings = embeddings;
      this.version = version;
      await saveCache(this.clientId, version, queries, embeddings);

      this.initialized = true;
      this.initializing = false;
      return { fromCache: false, queryCount: queries.length };
    } catch (error) {
      console.error(`📚 [LOCAL-AC] Initialization failed:`, error);
      this.initializing = false;
      throw error;
    }
  }

  async search(query, limit = 5, threshold = THRESHOLD_HIGH) {
    if (!this.initialized) {
      debug(`📚 [LOCAL-AC] Not initialized, initializing now...`);
      await this.initialize();
    }

    if (!this.queries || this.queries.length === 0) {
      debug(`📚 [LOCAL-AC] No queries to search`);
      return { suggestions: [], timing_ms: 0 };
    }

    const result = await searchLocal(
      normalizeQueryForMatching(query, this.clientId),
      this.queries,
      this.embeddings,
      limit,
      threshold,
    );

    return {
      suggestions: result.results.map((r) => ({
        answer_id: r.answer_id,
        title: r.title,
        summary: r.summary,
        similarity: r.similarity,
        canonical_query: r.canonical_query,
      })),
      timing_ms: result.timing.total_ms,
      timing_detail: result.timing,
    };
  }

  isModelLoaded() {
    return iframeReady;
  }

  async preloadModel() {
    debug(`📚 [LOCAL-AC] Preloading model...`);
    await initIframe();
    await iframeRequest('init');
    debug(`📚 [LOCAL-AC] Model preloaded`);
  }
}

// Singleton instance cache
const instances = new Map();

export function getLocalAutocomplete(clientId) {
  if (!instances.has(clientId)) {
    instances.set(clientId, new LocalAutocomplete(clientId));
  }
  return instances.get(clientId);
}
